import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { wallApi } from '../api/api';
import MatrixBackground from '../components/wall/MatrixBackground';
import PostBubble from '../components/wall/PostBubble';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { 
  Plus, Settings, Users, Heart, FileText, Image, Video, Link as LinkIcon,
  Loader2, Sparkles, Zap, Search, ArrowLeft, Upload, X, File, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/layout/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve wall profile image to a publicly accessible URL
const getWallAvatarUrl = (wall) => {
  if (!wall?.profile_image) return null;
  const img = wall.profile_image;
  // If it's already a full URL or a local static file, use as-is
  if (img.startsWith('http') || (img.startsWith('/') && !img.startsWith('/api/'))) return img;
  // For /api/walls/file/ paths, use the public avatar endpoint
  if (wall.slug) return `${BACKEND_URL}/api/walls/avatar/${wall.slug}`;
  return `${BACKEND_URL}${img}`;
};

export default function WallPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const avatarInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [loading, setLoading] = useState(true);
  const [wallAccess, setWallAccess] = useState(null);
  const [myWall, setMyWall] = useState(null);
  const [posts, setPosts] = useState([]);
  const [feed, setFeed] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [discoverWalls, setDiscoverWalls] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('feed');

  // Create post state
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState({ post_type: 'text', content: '', media_urls: [], link_url: '' });
  const [creating, setCreating] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [wallSettings, setWallSettings] = useState({ name: '', slug: '', description: '', profile_image: '' });
  const [saving, setSaving] = useState(false);

  // Check for payment success
  useEffect(() => {
    const success = searchParams.get('success');
    const sessionId = searchParams.get('session_id');

    if (success === 'true' && sessionId) {
      verifyPurchase(sessionId);
    }
  }, [searchParams]);

  const verifyPurchase = async (sessionId) => {
    try {
      const result = await wallApi.verifyPurchase(sessionId);
      if (result.success) {
        toast.success('Wall storage activated! You can now create your wall.');
        loadWallData();
        // Clear URL params
        navigate('/wall', { replace: true });
      }
    } catch (error) {
      console.error('Error verifying purchase:', error);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadWallData();
    }
  }, [authLoading, isAuthenticated]);

  const loadWallData = async () => {
    try {
      setLoading(true);

      // Check wall access
      const access = await wallApi.getAccess();
      setWallAccess(access);

      if (access.has_wall) {
        // Load my wall and posts
        const wall = await wallApi.getMyWall();
        setMyWall(wall);
        setWallSettings({
          name: wall.name,
          slug: wall.slug,
          description: wall.description || '',
          profile_image: wall.profile_image || ''
        });

        const postsData = await wallApi.getWallPosts(wall.slug);
        setPosts(postsData.posts);
      }

      // Load feed
      const feedData = await wallApi.getFeed();
      setFeed(feedData.posts);

      // Load subscriptions
      const subsData = await wallApi.getMySubscriptions();
      setSubscriptions(subsData.walls);

      // Load discover
      const discover = await wallApi.discoverWalls();
      setDiscoverWalls(discover.walls);

    } catch (error) {
      console.error('Error loading wall data:', error);
      toast.error('Failed to load wall data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseStorage = async () => {
    try {
      const result = await wallApi.purchaseStorage(window.location.origin);
      window.location.href = result.url;
    } catch (error) {
      toast.error('Failed to start checkout');
    }
  };

  const handleCreateWall = async () => {
    if (!wallSettings.name) {
      toast.error('Please enter a name for your wall');
      return;
    }

    try {
      setSaving(true);
      const wall = await wallApi.createWall({
        name: wallSettings.name,
        slug: wallSettings.slug || undefined,
        description: wallSettings.description,
        profile_image: wallSettings.profile_image
      });
      setMyWall(wall);
      setWallAccess({ ...wallAccess, has_wall: true, wall_slug: wall.slug });
      toast.success('Your wall has been created!');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create wall');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWall = async () => {
    try {
      setSaving(true);
      const updated = await wallApi.updateMyWall(wallSettings);
      setMyWall(updated);
      toast.success('Wall settings updated');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wall');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/quicktime',
      'application/pdf', 'text/plain'
    ];
    
    for (const file of fileArray) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 50MB per file.`);
        continue;
      }
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Unsupported file type.`);
        continue;
      }
      
      const fileId = `${file.name}-${Date.now()}`;
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      let preview = null;
      if (isImage) {
        preview = URL.createObjectURL(file);
      }
      
      try {
        setUploadingFiles(prev => [...prev, { id: fileId, name: file.name, progress: 0, preview, isImage, isVideo }]);
        
        const result = await wallApi.uploadFile(file, (progress) => {
          setUploadingFiles(prev => 
            prev.map(f => f.id === fileId ? { ...f, progress } : f)
          );
        });
        
        const fullUrl = `${BACKEND_URL}${result.url}`;
        setNewPost(prev => ({
          ...prev,
          post_type: isVideo ? 'video' : (isImage ? 'photo' : prev.post_type),
          media_urls: [...prev.media_urls, fullUrl]
        }));
        
        toast.success(`${file.name} uploaded!`);
        
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleAvatarUpload = async (files) => {
    const file = files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    try {
      setUploadingAvatar(true);
      const result = await wallApi.uploadFile(file, () => {});
      // Store the internal file URL — the backend avatar endpoint will resolve it
      setWallSettings(prev => ({ ...prev, profile_image: result.url }));
      toast.success('Profile image uploaded! Save to apply.');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) handleAvatarUpload([file]);
        return;
      }
    }
  };


  const handleRemoveMedia = (urlToRemove) => {
    setNewPost(prev => ({
      ...prev,
      media_urls: prev.media_urls.filter(url => url !== urlToRemove)
    }));
  };

  const handleCreatePost = async () => {
    if (!newPost.content && !newPost.media_urls.length && !newPost.link_url) {
      toast.error('Please add some content to your post');
      return;
    }

    try {
      setCreating(true);
      const post = await wallApi.createPost(newPost);
      setPosts([post, ...posts]);
      setNewPost({ post_type: 'text', content: '', media_urls: [], link_url: '' });
      setShowCreatePost(false);
      toast.success('Post created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const result = await wallApi.toggleLike(postId);
      
      // Update posts list
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, is_liked: result.liked, like_count: result.like_count } : p
      ));
      
      // Update feed list
      setFeed(feed.map(p => 
        p.id === postId ? { ...p, is_liked: result.liked, like_count: result.like_count } : p
      ));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      await wallApi.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleSubscribe = async (slug) => {
    try {
      await wallApi.subscribe(slug);
      toast.success('Subscribed!');
      loadWallData();
    } catch (error) {
      toast.error('Failed to subscribe');
    }
  };

  const handleSearch = async () => {
    try {
      const results = await wallApi.discoverWalls(searchQuery);
      setDiscoverWalls(results.walls);
    } catch (error) {
      toast.error('Search failed');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <MatrixBackground opacity={0.1} />
        <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  // No wall access - show upgrade page
  if (!wallAccess?.has_access) {
    const upgradePlans = wallAccess?.upgrade_plans || [];
    const currentPlan = wallAccess?.current_plan;
    
    return (
      <div className="min-h-screen bg-[#0a0a18]">
        <MatrixBackground opacity={0.15} />
        <Header />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 py-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-[#6D4AFF] to-[#00bcd4] bg-clip-text text-transparent">
                My Patriot Wall
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Your personal broadcast channel. Share updates, photos, videos, and files with your subscribers - 
              like your own private social network within MyPatriotMail.
            </p>
          </div>

          {/* What is Wall? */}
          <Card className="bg-[#1a1a2e]/80 backdrop-blur-xl border-[#6D4AFF]/30 mb-8">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#6D4AFF]" />
                What can you do with your Wall?
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-[#252540]/50 rounded-xl p-4">
                  <FileText className="w-8 h-8 text-[#6D4AFF] mb-2" />
                  <h4 className="font-medium text-white mb-1">Share Updates</h4>
                  <p className="text-sm text-gray-400">Post text updates, announcements, and thoughts with your community</p>
                </div>
                <div className="bg-[#252540]/50 rounded-xl p-4">
                  <Image className="w-8 h-8 text-[#00bcd4] mb-2" />
                  <h4 className="font-medium text-white mb-1">Media Gallery</h4>
                  <p className="text-sm text-gray-400">Upload and share photos, videos, and files with subscribers</p>
                </div>
                <div className="bg-[#252540]/50 rounded-xl p-4">
                  <Users className="w-8 h-8 text-[#4ade80] mb-2" />
                  <h4 className="font-medium text-white mb-1">Build Community</h4>
                  <p className="text-sm text-gray-400">Grow your subscriber base and engage with likes and follows</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Plan Info */}
          {currentPlan && (
            <div className="bg-[#252540]/50 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Your current plan</p>
                <p className="text-white font-semibold">{currentPlan.name}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">Wall Storage</p>
                <p className="text-white font-semibold">
                  {currentPlan.wall_storage_gb > 0 ? `${currentPlan.wall_storage_gb}GB` : 'Not included'}
                </p>
              </div>
            </div>
          )}

          {/* Upgrade Plans */}
          <h3 className="text-xl font-semibold text-white mb-4 text-center">
            Upgrade to unlock your Wall
          </h3>
          
          {upgradePlans.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upgradePlans.map((plan) => (
                <Card 
                  key={plan.id} 
                  className="bg-[#1a1a2e]/80 backdrop-blur-xl border-[#6D4AFF]/30 hover:border-[#6D4AFF]/60 transition-all hover:shadow-[0_0_30px_rgba(109,74,255,0.2)]"
                >
                  <CardHeader>
                    <CardTitle className="text-white">{plan.name}</CardTitle>
                    <CardDescription className="text-gray-400">
                      {plan.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-white">
                        ${plan.monthly_price}
                        <span className="text-sm font-normal text-gray-400">/mo</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        or ${plan.yearly_price}/year
                      </div>
                    </div>
                    
                    <div className="bg-[#6D4AFF]/10 rounded-lg p-3 mb-4">
                      <div className="flex items-center gap-2 text-[#6D4AFF]">
                        <Sparkles className="w-4 h-4" />
                        <span className="font-medium">{plan.wall_storage_gb}GB Wall Storage</span>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => navigate('/register?plan=' + plan.id)}
                      className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Upgrade Now
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-[#1a1a2e]/80 backdrop-blur-xl border-[#6D4AFF]/30">
              <CardContent className="p-8 text-center">
                <p className="text-gray-400 mb-4">
                  No plans with Wall access are currently available. Contact support for more information.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Has access but no wall - show setup
  if (!wallAccess?.has_wall) {
    return (
      <div className="min-h-screen bg-[#0a0a18]">
        <MatrixBackground opacity={0.15} />
        <Header />
        
        <div className="relative z-10 max-w-2xl mx-auto px-4 py-12">
          <Card className="bg-[#1a1a2e]/80 backdrop-blur-xl border-[#6D4AFF]/30">
            <CardHeader>
              <CardTitle className="text-2xl text-white">Create Your Wall</CardTitle>
              <CardDescription className="text-gray-400">
                Set up your personal blog wall
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Wall Name</Label>
                <Input
                  value={wallSettings.name}
                  onChange={(e) => setWallSettings({ ...wallSettings, name: e.target.value })}
                  placeholder="My Awesome Wall"
                  className="bg-[#252540] border-[#2d2d44] text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Custom URL (optional)</Label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">mypatriotmail.org/wall/</span>
                  <Input
                    value={wallSettings.slug}
                    onChange={(e) => setWallSettings({ ...wallSettings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9\-\.]/g, '') })}
                    placeholder="j.smith"
                    className="bg-[#252540] border-[#2d2d44] text-white flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">Leave blank to use default (first-initial.last-name)</p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Description (HTML allowed)</Label>
                <Textarea
                  value={wallSettings.description}
                  onChange={(e) => setWallSettings({ ...wallSettings, description: e.target.value })}
                  placeholder="Tell visitors about your wall..."
                  className="bg-[#252540] border-[#2d2d44] text-white min-h-24"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Profile Picture</Label>
                <div
                  onClick={() => avatarInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files?.length) handleAvatarUpload(e.dataTransfer.files); }}
                  onPaste={handleAvatarPaste}
                  tabIndex={0}
                  className="border-2 border-dashed border-[#2d2d44] hover:border-[#6D4AFF]/50 focus:border-[#6D4AFF] focus:outline-none rounded-xl p-4 cursor-pointer transition-all flex items-center gap-4"
                  data-testid="avatar-upload-zone"
                >
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => { if (e.target.files?.length) handleAvatarUpload(e.target.files); e.target.value = ''; }}
                    className="hidden"
                  />
                  {wallSettings.profile_image ? (
                    <img src={wallSettings.profile_image} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-[#6D4AFF]" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-[#252540] flex items-center justify-center">
                      <Upload className="w-6 h-6 text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1">
                    {uploadingAvatar ? (
                      <div className="flex items-center gap-2 text-sm text-[#6D4AFF]">
                        <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-300">{wallSettings.profile_image ? 'Click to change' : 'Drop, paste, or click to upload'}</p>
                        <p className="text-xs text-gray-500">JPG, PNG, GIF — max 5MB — Ctrl+V to paste screenshot</p>
                      </>
                    )}
                  </div>
                  {wallSettings.profile_image && (
                    <button onClick={(e) => { e.stopPropagation(); setWallSettings({ ...wallSettings, profile_image: '' }); }} className="text-gray-500 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <Button
                onClick={handleCreateWall}
                disabled={saving || !wallSettings.name || uploadingAvatar}
                className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Wall
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main wall dashboard
  return (
    <div className="min-h-screen bg-[#0a0a18]">
      <MatrixBackground opacity={0.1} />
      <Header />
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 py-6">
        {/* Wall Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {getWallAvatarUrl(myWall) ? (
              <img 
                src={getWallAvatarUrl(myWall)} 
                alt={myWall.name} 
                className="w-16 h-16 rounded-full object-cover border-2 border-[#6D4AFF]"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#00bcd4] flex items-center justify-center text-white text-2xl font-bold">
                {myWall?.name?.charAt(0) || 'W'}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">{myWall?.name}</h1>
              <p className="text-gray-400 text-sm">@{myWall?.slug}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-4 text-gray-400 text-sm">
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {myWall?.subscriber_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {myWall?.post_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="w-4 h-4" />
                {myWall?.total_likes || 0}
              </span>
            </div>
            
            <Button 
              onClick={() => setShowSettings(true)}
              variant="outline" 
              size="icon"
              className="border-[#6D4AFF]/50 text-[#6D4AFF] hover:bg-[#6D4AFF]/10"
            >
              <Settings className="w-4 h-4" />
            </Button>
            
            <Button 
              onClick={() => setShowCreatePost(true)}
              className="bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]"
              data-testid="new-post-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#1a1a2e] border border-[#2d2d44] mb-6">
            <TabsTrigger value="feed" className="data-[state=active]:bg-[#6D4AFF]">Feed</TabsTrigger>
            <TabsTrigger value="my-wall" className="data-[state=active]:bg-[#6D4AFF]">My Wall</TabsTrigger>
            <TabsTrigger value="discover" className="data-[state=active]:bg-[#6D4AFF]">Discover</TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-[#6D4AFF]">Subscriptions</TabsTrigger>
          </TabsList>

          {/* Feed Tab */}
          <TabsContent value="feed">
            {feed.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No posts in your feed yet.</p>
                <p className="text-sm">Subscribe to walls to see their posts here!</p>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto space-y-4">
                {feed.map(post => (
                  <PostBubble 
                    key={post.id} 
                    post={post} 
                    onLike={handleLike}
                    showWallInfo
                    wallName={post.wall_name}
                    wallProfileImage={post.wall_profile_image}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Wall Tab */}
          <TabsContent value="my-wall">
            {posts.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No posts yet. Create your first post!</p>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto space-y-4">
                {posts.map(post => (
                  <PostBubble 
                    key={post.id} 
                    post={post} 
                    onLike={handleLike}
                    onDelete={handleDeletePost}
                    isOwner
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Discover Tab */}
          <TabsContent value="discover">
            <div className="mb-6 flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search walls..."
                className="bg-[#1a1a2e] border-[#2d2d44] text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="outline" className="border-[#6D4AFF]/50">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {discoverWalls.map(wall => (
                <Card 
                  key={wall.id} 
                  className="bg-[#1a1a2e]/80 border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/wall/${wall.slug}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      {getWallAvatarUrl(wall) ? (
                        <img src={getWallAvatarUrl(wall)} alt={wall.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#00bcd4] flex items-center justify-center text-white font-bold">
                          {wall.name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white truncate">{wall.name}</p>
                        <p className="text-sm text-gray-400">@{wall.slug}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">
                        {wall.subscriber_count} subscribers
                      </span>
                      <div className="flex items-center gap-2">
                        {!wall.is_owner && !wall.is_subscribed && (
                          <Button 
                            onClick={(e) => { e.stopPropagation(); handleSubscribe(wall.slug); }}
                            size="sm"
                            className="bg-[#6D4AFF]"
                          >
                            Subscribe
                          </Button>
                        )}
                        {wall.is_subscribed && !wall.is_owner && (
                          <span className="text-xs text-[#6D4AFF]">Subscribed</span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/wall/${wall.slug}`); }}
                          className="border-[#6D4AFF]/40 text-[#6D4AFF] hover:bg-[#6D4AFF]/10"
                        >
                          Visit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            {subscriptions.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>You haven't subscribed to any walls yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subscriptions.map(wall => (
                  <Card 
                    key={wall.id} 
                    className="bg-[#1a1a2e]/80 border-[#2d2d44] cursor-pointer hover:border-[#6D4AFF]/50"
                    onClick={() => navigate(`/wall/${wall.slug}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {getWallAvatarUrl(wall) ? (
                          <img src={getWallAvatarUrl(wall)} alt={wall.name} className="w-12 h-12 rounded-full object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#00bcd4] flex items-center justify-center text-white font-bold">
                            {wall.name.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-white">{wall.name}</p>
                          <p className="text-sm text-gray-400">{wall.post_count} posts</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Post</DialogTitle>
            <DialogDescription className="text-gray-500">Share an update with your wall subscribers</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              {['text', 'photo', 'video', 'link'].map(type => (
                <Button
                  key={type}
                  variant={newPost.post_type === type ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewPost({ ...newPost, post_type: type })}
                  className={newPost.post_type === type ? 'bg-[#6D4AFF]' : 'border-[#2d2d44] text-gray-400'}
                  data-testid={`post-type-${type}`}
                >
                  {type === 'text' && <FileText className="w-4 h-4 mr-1" />}
                  {type === 'photo' && <Image className="w-4 h-4 mr-1" />}
                  {type === 'video' && <Video className="w-4 h-4 mr-1" />}
                  {type === 'link' && <LinkIcon className="w-4 h-4 mr-1" />}
                  <span className="capitalize">{type}</span>
                </Button>
              ))}
            </div>

            <Textarea
              value={newPost.content}
              onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
              placeholder="What's on your mind?"
              className="bg-[#252540] border-[#2d2d44] text-white min-h-32"
              data-testid="post-content-input"
            />

            {/* File Upload Area - shown for photo/video/text types */}
            {newPost.post_type !== 'link' && (
              <div className="space-y-3">
                {/* Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                    isDragging
                      ? 'border-[#6D4AFF] bg-[#6D4AFF]/10'
                      : 'border-[#2d2d44] hover:border-[#6D4AFF]/50 hover:bg-[#252540]/50'
                  }`}
                  data-testid="file-drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.txt,.doc,.docx"
                    onChange={(e) => {
                      if (e.target.files?.length) handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-[#6D4AFF]' : 'text-gray-500'}`} />
                  <p className="text-sm text-gray-400">
                    {isDragging ? 'Drop files here' : 'Drag & drop files or click to browse'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Images, videos, PDFs up to 50MB each
                  </p>
                </div>

                {/* Upload Progress */}
                {uploadingFiles.length > 0 && (
                  <div className="space-y-2" data-testid="upload-progress-area">
                    {uploadingFiles.map(f => (
                      <div key={f.id} className="bg-[#252540] rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-300 truncate max-w-[200px]">{f.name}</span>
                          <span className="text-xs text-[#6D4AFF]">{f.progress}%</span>
                        </div>
                        <Progress value={f.progress} className="h-1.5" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Uploaded Media Previews */}
                {newPost.media_urls.length > 0 && (
                  <div className="space-y-2" data-testid="media-previews">
                    <Label className="text-gray-400 text-xs">Attached Media ({newPost.media_urls.length})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {newPost.media_urls.map((url, idx) => (
                        <div key={idx} className="relative group/preview rounded-lg overflow-hidden bg-[#252540] aspect-square">
                          {url.match(/\.(mp4|webm|mov)/) || newPost.post_type === 'video' ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="w-8 h-8 text-gray-400" />
                              <span className="absolute bottom-1 left-1 text-[10px] text-gray-500 truncate max-w-[80px]">
                                video
                              </span>
                            </div>
                          ) : url.match(/\.(pdf|txt|doc|docx)/) ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <File className="w-8 h-8 text-gray-400" />
                              <span className="absolute bottom-1 left-1 text-[10px] text-gray-500 truncate max-w-[80px]">
                                file
                              </span>
                            </div>
                          ) : (
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          )}
                          <button
                            onClick={() => handleRemoveMedia(url)}
                            className="absolute top-1 right-1 bg-black/70 rounded-full p-1 opacity-0 group-hover/preview:opacity-100 transition-opacity"
                            data-testid={`remove-media-${idx}`}
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {newPost.post_type === 'link' && (
              <div className="space-y-2">
                <Label className="text-gray-300">Link URL</Label>
                <Input
                  value={newPost.link_url}
                  onChange={(e) => setNewPost({ ...newPost, link_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-[#252540] border-[#2d2d44] text-white"
                  data-testid="post-link-input"
                />
              </div>
            )}

            <Button 
              onClick={handleCreatePost}
              disabled={creating || uploadingFiles.length > 0}
              className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]"
              data-testid="submit-post-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {uploadingFiles.length > 0 ? 'Uploading...' : 'Post'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Wall Settings</DialogTitle>
            <DialogDescription className="text-gray-500">Customize your wall appearance</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Wall Name</Label>
              <Input
                value={wallSettings.name}
                onChange={(e) => setWallSettings({ ...wallSettings, name: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-300">Custom URL</Label>
              <Input
                value={wallSettings.slug}
                onChange={(e) => setWallSettings({ ...wallSettings, slug: e.target.value.toLowerCase().replace(/[^a-z0-9\-\.]/g, '') })}
                className="bg-[#252540] border-[#2d2d44] text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea
                value={wallSettings.description}
                onChange={(e) => setWallSettings({ ...wallSettings, description: e.target.value })}
                className="bg-[#252540] border-[#2d2d44] text-white min-h-24"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Profile Picture</Label>
              <div
                onClick={() => avatarInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files?.length) handleAvatarUpload(e.dataTransfer.files); }}
                onPaste={handleAvatarPaste}
                tabIndex={0}
                className="border-2 border-dashed border-[#2d2d44] hover:border-[#6D4AFF]/50 focus:border-[#6D4AFF] focus:outline-none rounded-xl p-4 cursor-pointer transition-all flex items-center gap-4"
                data-testid="settings-avatar-upload-zone"
              >
                {wallSettings.profile_image ? (
                  <img src={wallSettings.profile_image} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-[#6D4AFF]" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#252540] flex items-center justify-center">
                    <Upload className="w-6 h-6 text-gray-500" />
                  </div>
                )}
                <div className="flex-1">
                  {uploadingAvatar ? (
                    <div className="flex items-center gap-2 text-sm text-[#6D4AFF]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-300">{wallSettings.profile_image ? 'Click to change' : 'Drop image or click to upload'}</p>
                      <p className="text-xs text-gray-500">JPG, PNG, GIF — max 5MB</p>
                    </>
                  )}
                </div>
                {wallSettings.profile_image && (
                  <button onClick={(e) => { e.stopPropagation(); setWallSettings({ ...wallSettings, profile_image: '' }); }} className="text-gray-500 hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <Button 
              onClick={handleUpdateWall}
              disabled={saving}
              className="w-full bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
